import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MessageCircle, Check, ExternalLink, Send } from "lucide-react";
import { toast } from "sonner";
import { CostItemsField } from "./CostItemsField";

const DELIVERY_METHOD_OPTIONS = [
  ["pos_java", "1. Pos Indonesia — within Java (Rp 19,000)"],
  ["pos_outside_java", "2. Pos Indonesia — outside Java (Rp 29,000)"],
  ["jnt_java", "3. JNT — within Java (Rp 19,000)"],
  ["jnt_outside_java", "4. JNT — outside Java (Rp 29,000)"],
  ["self_pickup_gojek", "5. Self-pickup by Gojek/Grab (customer pays)"],
  ["self_pickup_free", "6. Self-Pickup (free)"],
];

const CONTEXT_FIELDS = {
  initial_condition:     [{ key: "initial_condition", label: "Initial condition notes", type: "textarea", persist: true }],
  cost_confirmation:     [{ key: "cost_items", label: "Cost items (up to 5 rows — leave a row blank to skip it)", type: "items", persist: true }],
  repair_result:         [{ key: "repair_result", label: "Result", type: "select", options: [ ["success","success"], ["failed","failed"] ], persist: true }],
  qc_report:             [{ key: "qc_notes", label: "QC report notes", type: "textarea", persist: true }],
  waiting_for_payment:   [{ key: "delivery_method", label: "Delivery method selected by customer", type: "select", options: DELIVERY_METHOD_OPTIONS, persist: true }],
  payment_request:       [
    { key: "payment_amount", label: "Payment amount (IDR)", type: "number", persist: true },
    { key: "payment_method", label: "Payment method", type: "text", persist: true, placeholder: "BCA / GoPay / Cash on pickup", default: "BCA" },
    { key: "payment_ref",    label: "Payment reference / VA number", type: "text", persist: true, default: "HARI CHRISTANTO - 124 0880 888" },
  ],
  shipped_with_tracking: [
    { key: "courier", label: "Courier", type: "text", persist: true, persistKey: "tracking_courier", placeholder: "JNE / SiCepat / J&T" },
    { key: "tracking_number", label: "Tracking number", type: "text", persist: true, placeholder: "e.g. JX1234567890" },
  ],
};

export function RepairStageCard({ stage, intake, onLogged, onIntakeSaved }) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({});
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const fields = useMemo(() => CONTEXT_FIELDS[stage.key] || [], [stage.key]);
  const history = (intake.wa_notifications || []).filter((n) => n.stage === stage.key);
  const last = history[history.length - 1];

  useEffect(() => {
    // Preload persisted values from intake if available, otherwise fall back to the field's default.
    const v = {};
    for (const f of fields) {
      const src = f.persistKey || f.key;
      if (intake[src] !== undefined && intake[src] !== null && intake[src] !== "") {
        v[f.key] = intake[src];
      } else if (f.default !== undefined) {
        v[f.key] = f.default;
      }
    }
    setValues(v);
  }, [intake, stage.key, fields]);

  const buildPreview = async () => {
    if (stage.key === "waiting_for_payment" && !values.delivery_method) {
      toast.error("Please select the delivery method the customer chose first");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/admin/repair-intakes/${intake.id}/wa-preview`, {
        stage: stage.key,
        context: values,
      });
      setPreview(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const savePersistedFields = async () => {
    const patch = {};
    for (const f of fields) {
      if (!f.persist) continue;
      const target = f.persistKey || f.key;
      const val = values[f.key];
      if (val === undefined || val === "") continue;
      patch[target] = f.type === "number" ? Number(val) : val;
    }
    if (Object.keys(patch).length === 0) return true;
    try {
      const { data } = await api.patch(`/admin/repair-intakes/${intake.id}`, patch);
      onIntakeSaved?.(data);
      return true;
    } catch (err) {
      toast.warning("Message sent, but couldn't save these fields to the intake. Please re-enter and retry.");
      return false;
    }
  };

  const openWa = async () => {
    if (!preview) {
      toast.error("Please preview the message first");
      return;
    }
    // Open the WhatsApp link synchronously, before any await, so the click's
    // user-gesture is still active when the popup is created (browsers silently
    // block window.open() calls that happen after an async delay).
    window.open(preview.wa_link, "_blank", "noopener,noreferrer");
    await savePersistedFields();
    // Log that we clicked send.
    try {
      const { data } = await api.post(`/admin/repair-intakes/${intake.id}/wa-log`, { stage: stage.key });
      onLogged?.(data.entry);
      toast.success(`Sent · ${stage.label_en}`);
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const openDialog = () => {
    setPreview(null);
    setOpen(true);
    // Auto-preview if no context fields
    if (fields.length === 0) {
      setTimeout(() => buildPreview(), 0);
    }
  };

  return (
    <>
      <div data-testid={`wa-stage-card-${stage.key}`} className={`rounded-2xl border p-4 flex items-start justify-between gap-3 ${last ? "border-emerald-200 bg-emerald-50/40" : "border-zinc-200 bg-white"}`}>
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-500">{stage.label_id}</div>
          <div className="font-display font-bold text-zinc-900 text-sm">{stage.label_en}</div>
          {last && (
            <div className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1">
              <Check className="w-3 h-3" /> Sent · {new Date(last.sent_at).toLocaleString()}
              {history.length > 1 && <span className="text-zinc-400 ml-1">({history.length}×)</span>}
            </div>
          )}
        </div>
        <Button data-testid={`wa-stage-btn-${stage.key}`} onClick={openDialog} size="sm" className={`rounded-full text-xs ${last ? "bg-zinc-100 text-zinc-800 hover:bg-zinc-200" : "bg-brand hover:bg-brand-hover text-white"}`}>
          {last ? "Resend" : "Send"}
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-brand" /> {stage.label_id} · <span className="text-zinc-500 font-normal text-sm">{stage.label_en}</span>
            </DialogTitle>
          </DialogHeader>

          {fields.length > 0 && (
            <div className="space-y-3 mt-2">
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Message details</div>
              {fields.map((f) => (
                <div key={f.key}>
                  <Label className="text-sm font-semibold text-zinc-800">{f.label}</Label>
                  {f.type === "items" ? (
                    <CostItemsField testid={`wa-field-${f.key}`} value={values[f.key] || []} onChange={(rows) => setValues((v) => ({ ...v, [f.key]: rows }))} />
                  ) : f.type === "textarea" ? (
                    <Textarea data-testid={`wa-field-${f.key}`} value={values[f.key] || ""} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} rows={3} className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200" placeholder={f.placeholder} />
                  ) : f.type === "select" ? (
                    <select data-testid={`wa-field-${f.key}`} value={values[f.key] || ""} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} className="mt-1.5 rounded-xl bg-zinc-50 border border-zinc-200 h-10 px-3 w-full text-sm">
                      <option value="">— select —</option>
                      {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  ) : (
                    <Input data-testid={`wa-field-${f.key}`} type={f.type} value={values[f.key] || ""} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200 h-10" placeholder={f.placeholder} />
                  )}
                </div>
              ))}
              <Button data-testid="wa-preview-btn" type="button" onClick={buildPreview} disabled={loading} variant="outline" className="rounded-full">
                {loading ? "Building…" : "Preview message"}
              </Button>
            </div>
          )}

          {preview && (
            <div className="mt-4">
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">Message preview (ID + EN)</div>
              <pre data-testid="wa-preview-body" className="bg-zinc-50 border border-zinc-200 rounded-2xl p-4 text-xs whitespace-pre-wrap font-mono text-zinc-800 max-h-72 overflow-y-auto">{preview.message}</pre>
              <div className="mt-2 text-xs text-zinc-500">
                Recipient: <span className="font-mono">{preview.phone || "(no phone on file)"}</span>
              </div>
              <div className="mt-4 flex gap-2">
                <Button data-testid="wa-send-btn" onClick={openWa} className="rounded-full bg-brand hover:bg-brand-hover text-white flex-1">
                  <Send className="w-4 h-4 mr-1.5" /> Open WhatsApp & log
                </Button>
                <a href={preview.wa_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800">
                  <ExternalLink className="w-3 h-3" /> link only
                </a>
              </div>
            </div>
          )}

          {fields.length === 0 && !preview && (
            <div className="text-sm text-zinc-500 mt-2">Building preview…</div>
          )}

          {history.length > 0 && (
            <div className="mt-6 pt-4 border-t border-zinc-100">
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">History</div>
              <ul className="space-y-1 text-xs text-zinc-600">
                {history.slice().reverse().map((h) => (
                  <li key={`${h.stage}-${h.sent_at}`} className="flex items-center gap-2">
                    <Check className="w-3 h-3 text-emerald-600" />
                    {new Date(h.sent_at).toLocaleString()} · by {h.admin_name || h.admin_id}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
